// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — inscriptions d'une campagne : liste paginée (état, quantités, reste à charge figé à
// l'inscription, remboursement), inscription au guichet pour un salarié présent (récapitulatif calculé par le
// serveur, puis accord du salarié ; jamais pour soi), vérification (≠ saisie), désistement par le bureau (motif,
// avant toute remise), levée d'une attribution non retirée, export nominatif tracé.
// Routes : GET /:id/inscriptions, POST /:id/inscriptions/simulation, POST /:id/inscriptions (Idempotency-Key),
// POST /inscriptions/:id/{verifier,desister,lever-attribution}, GET /:id/etat.csv.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Calculator, CheckCircle2, ClipboardList, MoreVertical, Undo2, UserPlus, XCircle } from 'lucide-react';
import { Bouton, BandeauInfo, MenuActions } from '../../design/composants';
import { toArray } from './api';
import { Champ, Etat, PaginationDs } from './commun';
import { ChoixPersonne } from './FenetresMandat';
import { francs, moisLong, dateHeureLongue } from './apiSocial';
import { useLectureSociale, EtatsLecture, FiltreEtats, FenetreActe, MarqueDemo, motifValide, ChampMotif, erreurSociale } from './communSocial';
import { useCampagnes, quantiteUnites, ETATS_INSCRIPTION, CANAUX } from './apiCampagnes';
import { RecapitulatifServeur, BoutonExportEtat, codeEtat, lireEntier } from './communCampagnes';

const A_RECALCULER = ['JETON_PERIME', 'CONDITIONS_CHANGEES'];

export function FenetreGuichetInscription({ campagne, onFermer, onFait }) {
  const api = useCampagnes();
  const max = campagne.reglement?.quantite_max_salarie || null;
  const [personne, setPersonne] = useState(null);
  const [quantite, setQuantite] = useState('1');
  const [recap, setRecap] = useState(null);
  const [accord, setAccord] = useState(false);
  const [erreurSim, setErreurSim] = useState(null);
  const [enCalcul, setEnCalcul] = useState(false);
  const q = lireEntier(quantite);
  const saisieOk = personne && q && q >= 1 && (!max || q <= max);
  const oublier = () => { setRecap(null); setAccord(false); };
  const simuler = async () => {
    setEnCalcul(true); setErreurSim(null); oublier();
    try { setRecap(await api.simulerInscription(campagne.id, { employe_id: personne.id, reponses: { quantite: q } })); } catch (e) { setErreurSim(erreurSociale(e)); }
    setEnCalcul(false);
  };
  return (
    <FenetreActe titre="Inscription au guichet" Icone={UserPlus} libelleBouton="Enregistrer l'inscription" pret={!!(saisieOk && recap && accord)} large onFermer={onFermer}
      aide="Pour un salarié présent au guichet. Vous ne pouvez pas vous inscrire vous-même par cette voie : passez par votre espace salarié. Une inscription est une intention d'achat : aucune dette ne naît avant la remise."
      onConfirmer={async (cle) => {
        try {
          const r = await api.inscrire(campagne.id, { employe_id: personne.id, jeton: recap.jeton, reponses: { quantite: q, accord: true } }, cle);
          onFait(`Inscription ${r.inscription?.numero || ''} enregistrée pour ${quantiteUnites(campagne, q)}.`);
        } catch (e) {
          if (A_RECALCULER.includes(e?.response?.data?.code)) oublier();
          throw e;
        }
      }}>
      <ChoixPersonne choisie={personne} onChoisir={(p) => { setPersonne(p); oublier(); }} />
      <Champ libelle={`Nombre de ${campagne.unite_pluriel}`} requis aide={max ? `Entre 1 et ${max} selon le règlement ; la quantité restant disponible est contrôlée par le serveur.` : 'La quantité admise est contrôlée par le serveur.'}>
        <input className="rh-champ-saisie" type="number" inputMode="numeric" min={1} max={max || undefined} step={1} value={quantite} style={{ maxWidth: 160 }}
          onChange={(e) => { setQuantite(e.target.value); oublier(); }} />
      </Champ>
      <div style={{ marginBottom: 12 }}>
        <Bouton Icone={Calculator} disabled={!saisieOk || enCalcul} onClick={simuler}>Calculer le récapitulatif</Bouton>
      </div>
      {erreurSim && <BandeauInfo ton="alerte" titre="Inscription impossible en l'état">{erreurSim.message}{erreurSim.erreurs.length > 0 && ` (${erreurSim.erreurs.join(' ; ')})`}</BandeauInfo>}
      <RecapitulatifServeur recap={recap} />
      {recap && (
        <>
          {recap.engagement && (
            <blockquote style={{ margin: '0 0 12px', padding: '8px 12px', borderLeft: '3px solid var(--sg-primaire)', fontSize: 14, overflowWrap: 'anywhere' }}>{recap.engagement}</blockquote>
          )}
          {recap.expire_le && <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Récapitulatif valable jusqu'au {dateHeureLongue(recap.expire_le)} ; au-delà, il est recalculé.</p>}
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, marginBottom: 12 }}>
            <input type="checkbox" checked={accord} onChange={(e) => setAccord(e.target.checked)} style={{ marginTop: 3 }} />
            <span>Le salarié a lu ce récapitulatif et le texte d'engagement ci-dessus, et donne son accord.</span>
          </label>
        </>
      )}
    </FenetreActe>
  );
}

function FenetreVerifier({ inscription, onFermer, onFait }) {
  const api = useCampagnes();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`Vérifier l'inscription ${inscription.numero}`} Icone={CheckCircle2} libelleBouton="Vérifier" pret={!motif.trim() || motifValide(motif, 3)} onFermer={onFermer}
      aide="La personne qui a saisi l'inscription au guichet ne la vérifie pas (double regard)."
      onConfirmer={async () => { await api.verifierInscription(inscription.id, motif.trim() || undefined); onFait('Inscription vérifiée.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} requis={false} libelleChamp="Observation (facultative)" aide="Conservée dans l'historique." />
    </FenetreActe>
  );
}

function FenetreMotifInscription({ inscription, titre, aide, libelleBouton, minimum, Icone, appel, message, onFermer, onFait }) {
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`${titre} — ${inscription.numero}`} Icone={Icone} libelleBouton={libelleBouton} danger pret={motifValide(motif, minimum)} onFermer={onFermer} aide={aide}
      onConfirmer={async () => { await appel(inscription.id, motif.trim()); onFait(message); }}>
      <ChampMotif valeur={motif} onChange={setMotif} aide={`${minimum} caractères au moins. Le motif est conservé dans l'historique.`} />
    </FenetreActe>
  );
}

const FILTRES = Object.keys(ETATS_INSCRIPTION);

export default function InscriptionsVue({ campagne, droits }) {
  const api = useCampagnes();
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const liste = useLectureSociale(() => api.inscriptions(campagne.id, { page, taille: 25, statut }), [api, campagne.id, page, statut]);
  const elements = toArray(liste.donnees);
  const code = codeEtat(campagne);
  const fait = (m) => { setFenetre(null); setInfo(m); liste.recharger(); };
  const peutGuichet = droits.a('ds_inscription_saisir_guichet') && code === 'OUVERTE';
  const menu = (i) => {
    const s = i.etat?.code || i.statut;
    return [
      s === 'INSCRITE' && droits.a('ds_inscription_gerer') && { libelle: 'Vérifier', Icone: CheckCircle2, onClick: () => setFenetre({ type: 'verifier', i }) },
      ['INSCRITE', 'VERIFIEE', 'ATTRIBUEE'].includes(s) && !(Number(i.quantite_remise) > 0) && droits.a('ds_inscription_gerer')
        && { libelle: 'Enregistrer un désistement', Icone: Undo2, danger: true, onClick: () => setFenetre({ type: 'desister', i }) },
      ['ATTRIBUEE', 'PARTIELLEMENT_REMISE'].includes(s) && droits.a('ds_attribution_arreter')
        && { libelle: 'Lever l\'attribution', Icone: XCircle, danger: true, onClick: () => setFenetre({ type: 'lever', i }) },
    ].filter(Boolean);
  };
  return (
    <>
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <FiltreEtats table={ETATS_INSCRIPTION} codes={FILTRES} valeur={statut} onChange={(c) => { setStatut(c); setPage(1); }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {droits.a('ds_export_nominatif') && <BoutonExportEtat campagne={campagne} />}
          {peutGuichet && <Bouton variante="primaire" Icone={UserPlus} onClick={() => setFenetre({ type: 'guichet' })}>Inscrire au guichet</Bouton>}
        </div>
      </div>
      {droits.a('ds_inscription_saisir_guichet') && code !== 'OUVERTE' && (
        <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>L'inscription au guichet n'est possible que pendant l'ouverture des inscriptions.</p>
      )}
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={liste} Icone={ClipboardList} estVide={!elements.length}
          titreVide={statut ? 'Aucune inscription dans cet état' : 'Aucune inscription'}
          vide={statut ? 'Choisissez un autre état.' : 'Les inscriptions faites depuis l\'application, l\'espace salarié ou le guichet apparaissent ici, dans l\'ordre d\'inscription.'}>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead>
                <tr><th>Salarié</th><th>Quantité</th><th style={{ textAlign: 'right' }}>Reste à charge</th><th>Remboursement</th><th>État</th><th aria-label="Actions" /></tr>
              </thead>
              <tbody>
                {elements.map((i) => {
                  const items = menu(i);
                  return (
                    <tr key={i.id}>
                      <td>
                        <span className="rh-cellule-principale">{i.salarie}</span>
                        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{i.numero} · {i.matricule} · {CANAUX[i.canal] || i.canal} <MarqueDemo actif={i.donnees_demo} /></span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {quantiteUnites(campagne, i.quantite_demandee)}
                        {(i.quantite_attribuee !== null && i.quantite_attribuee !== undefined) && (
                          <span className="rh-cellule-secondaire" style={{ display: 'block' }}>attribué : {i.quantite_attribuee} · remis : {i.quantite_remise || 0}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {francs(i.reste_a_charge)}
                        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{francs(i.montant_total)} − aide {francs(i.montant_aide)}</span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {Number(i.reste_a_charge) > 0
                          ? <>{i.mensualite ? `${francs(i.mensualite)} par mois` : `${i.nb_mensualites} mois`}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{i.mensualite ? `${i.nb_mensualites} mois, ` : ''}à partir de {moisLong(i.premiere_periode)}{i.mensualite && i.derniere && Number(i.derniere) !== Number(i.mensualite) ? ` · dernière : ${francs(i.derniere)}` : ''}</span></>
                          : <span className="rh-cellule-secondaire">Rien à rembourser</span>}
                      </td>
                      <td>
                        <Etat table={ETATS_INSCRIPTION} code={i.etat?.code || i.statut} />
                        {i.motif_fin && <span className="rh-cellule-secondaire" style={{ display: 'block', maxWidth: 220 }}>{i.motif_fin}</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{items.length > 0 && <MenuActions iconeSeule gauche Icone={MoreVertical} libelle={`Actions sur l'inscription ${i.numero}`} items={items} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={liste.donnees} onPage={setPage} />
        </EtatsLecture>
      </section>
      <p className="rh-form-aide" style={{ margin: '8px 0 0' }}>Montants figés à l'inscription et calculés par le serveur ; l'export CSV est nominatif et tracé dans le journal d'audit.</p>
      {fenetre?.type === 'guichet' && <FenetreGuichetInscription campagne={campagne} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'verifier' && <FenetreVerifier inscription={fenetre.i} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'desister' && (
        <FenetreMotifInscription inscription={fenetre.i} titre="Désistement" libelleBouton="Enregistrer le désistement" minimum={3} Icone={Undo2}
          aide="Possible avant toute remise. Après une remise, un retour se déclare comme un écart de campagne. La quantité réservée est libérée."
          appel={api.desisterInscription} message="Désistement enregistré." onFermer={() => setFenetre(null)} onFait={fait} />
      )}
      {fenetre?.type === 'lever' && (
        <FenetreMotifInscription inscription={fenetre.i} titre="Lever l'attribution" libelleBouton="Lever l'attribution" minimum={5} Icone={XCircle}
          aide="Pour une attribution non retirée : aucune dette n'est créée, l'unité reste en stock. Si une partie a déjà été remise, seul le reste est libéré."
          appel={api.leverAttribution} message="Attribution levée." onFermer={() => setFenetre(null)} onFait={fait} />
      )}
    </>
  );
}
